import { type FC, useMemo, type MouseEvent, useCallback } from 'react';

import { AccoStars } from '@vakantiesnl/components/src/atoms/AccoStars';
import { Content } from '@vakantiesnl/components/src/atoms/Content';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AccommodationType } from '@vakantiesnl/components/src/molecules/AccommodationType';
import { LocationBreadCrumbs } from '@vakantiesnl/components/src/molecules/LocationBreadCrumbs';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { type BreadCrumbItem, type MicroCopy, type Search } from '@vakantiesnl/types';
import { isEqual } from 'lodash';

import routeData from '@RouteParams';

import { useCustomStyles } from './AccommodationHeader.style';
import { Separator } from '../../atoms/Separator';
import { isZoover } from '../../utils';
import { AccommodationTopBannerImage } from '../AccommodationTopBannerImage';

interface Props {
	microCopies: MicroCopy;
	toggleShareAccoModal: (event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>, isAccoHeader?: boolean) => void;
	/** Tracking event for breadcrumb clicks */
	onBreadCrumbClick?: (
		destinationUrl: string,
		destinationType?: BreadCrumbItem['destinationType'],
		isAccoHeader?: boolean,
		atomType?: string,
		label?: string,
	) => void;
	accommodation: Search.FullAccommodationItem;
	wrapperClassName?: string;
	isCentered?: boolean;
	isImageModalOpen?: boolean;
	adlinksCount?: number;
	/** Toggle callback to trigger closing the image modal */
	onCloseClick?: VoidFunction;
}

export const AccommodationHeader: FC<Props> = ({
	microCopies,
	toggleShareAccoModal,
	onBreadCrumbClick,
	accommodation,
	wrapperClassName,
	isCentered = false,
	isImageModalOpen,
	adlinksCount,
	onCloseClick,
}) => {
	const { classes, cx } = useCustomStyles({ isCentered });
	const { isDesktop } = useBreakpoints();
	const { location, name, starRating, type, formattedType, heliosId } = accommodation;
	const locationWithLinks = useMemo(() => location.map((locationItem) => ({ ...locationItem })), [location]);

	const accoTypeRoute = useMemo(() => {
		const keys = Object.keys(routeData.accoTypes) as Array<keyof typeof routeData.accoTypes>;
		return keys.filter((accoType) => accoType === type).map((accoType) => routeData.accoTypes[accoType])[0];
	}, [type]);

	/* Track the clicking of the accommodation review */
	const onAccoReviewLinkClick = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ reviewAccommodationClick }) =>
				track(
					reviewAccommodationClick(
						accommodation,
						isImageModalOpen != undefined && isImageModalOpen,
						adlinksCount,
					),
				),
			);
		});
	}, [accommodation, isImageModalOpen, adlinksCount]);

	const addReviewUrl = useMemo(() => `/review?entityId=${heliosId}&entityLevel=accommodation`, [heliosId]);

	const onShareAccoClick = useCallback(() => {
		onCloseClick && onCloseClick();
		toggleShareAccoModal(undefined, true);
	}, [onCloseClick, toggleShareAccoModal]);

	return (
		<Content className={cx(classes.container, wrapperClassName)}>
			{isZoover && <AccommodationTopBannerImage entityId={heliosId.toString()} name={name} />}
			<Typography variant={isDesktop ? 'heading2Xl' : 'headingXl'} as="h1" className={classes.header}>
				{name}
			</Typography>
			<div className={classes.basicInfo} data-cy="breadcrumbs">
				<div className={classes.accoTypeWrapper}>
					{!!formattedType && accoTypeRoute && (
						<AccommodationType
							accoType={formattedType}
							link={`/${accoTypeRoute}`}
							handleLinkClick={onBreadCrumbClick}
							className={classes.accoTypeLink}
						/>
					)}
					{!isEqual(starRating, 0) && (
						<>
							{!!formattedType && <Separator />}
							<AccoStars starsCount={starRating} />
						</>
					)}
				</div>
				<div className={classes.breadcrumbsWrapper}>
					{isDesktop && <Separator />}
					<LocationBreadCrumbs location={locationWithLinks} handleBreadcrumbClick={onBreadCrumbClick} />
					{isDesktop && <Separator />}
				</div>
				<Link icon="share" onClick={onShareAccoClick} className={classes.shareAccoLink} aria-label="Share">
					{microCopies['accommodation.shareAcco']}
				</Link>
				{isZoover && (
					<>
						{isDesktop && <Separator />}
						<Link className={classes.accoReview} href={addReviewUrl} onClick={onAccoReviewLinkClick}>
							<Icon type="editNav" />
							<Typography variant={'linkSm'} as={'span'}>
								{microCopies['accomodation.header.review']}
							</Typography>
						</Link>
					</>
				)}
			</div>
		</Content>
	);
};
